
<script>
import {
  required,
  sameAs,
  numeric,
  maxValue,
  minValue,
  email
} from "@vuelidate/validators";
import { tel, zipCode, birthdate, checked } from "@/utils/form-validators";
import Button from "@/components/atoms/button";
import Form from "@/components/molecules/form";
import FormGroup from "@/components/molecules/form-group";
import FormInput from "@/components/molecules/form-input";
import {
  formValidationProps,
  useFormValidation
} from "@/components/organisms/mixins/form-validation.mixin";
import FormRadio from "@/components/molecules/form-radio";
import FormSelect from "@/components/molecules/form-select";
import FormSelectOption from "@/components/molecules/form-select/form-select-option";
import FormSelectOptGroup from "@/components/molecules/form-select/form-select-optgroup";
import LinkTo from "@/components/atoms/link-to";
import FormCheckbox from "@/components/molecules/form-checkbox";
import FormError from "@/components/molecules/form/form-error";
import Captcha from "@/components/molecules/captcha";
import SuccessPopin from "@/components/organisms/forms/successPopin";
import FormFieldset from "@/components/molecules/form-fieldset";
import ButtonGroup from "@/components/molecules/button-group";
import { useForms } from "@/components/organisms/forms/mixin";

export default {
  name: "AskForMalinCardForm",
  components: {
    FormFieldset,
    ButtonGroup,
    Form,
    FormSelectOptGroup,
    FormSelect,
    FormGroup,
    FormError,
    FormSelectOption,
    FormInput,
    Button,
    FormRadio,
    FormCheckbox,
    LinkTo,
    Captcha,
    SuccessPopin
  },
  props: {
    magasins: {
      type: Array,
      required: true
    },
    magasinId: {
      type: Number,
      required: false
    },
    magasinsByCp: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    },
    ...formValidationProps()
  },
  emits: ["cp", "save"],
  setup(props) {
    const { redirectToPreviousPage } = useForms();
    const { captcha, v$, getState, getErrorMessage, checkValidation, states } =
      useFormValidation(props);

    return {
      redirectToPreviousPage,
      captcha,
      v$,
      getState,
      getErrorMessage,
      checkValidation,
      states
    };
  },
  data() {
    return {
      model: {
        possede_carte_cora: "",
        deja_possede_carte_malin: "",
        id_magasin: this.magasinId ? this.magasinId : "",
        nom: "",
        prenom: "",
        civilite: "",
        email: "",
        email_confirmation: "",
        tel: "",
        adresse: "",
        cp: "",
        ville: "",
        pays: "France",
        complement_adresse: "",
        date_naissance: "",
        nombre_enfants: "",
        date_naissance_1er_enfant: "",
        date_naissance_2eme_enfant: "",
        date_naissance_3eme_enfant: "",
        date_naissance_4eme_enfant: "",
        optin_email_suivi_informations_promotionnelles_commerciales: false,
        accepter_conditions_fidelite: false,
        accepter_conditions_compte_client: false
      },
      countries: ["France", "Belgique", "Suisse", "Allemagne", "Luxembourg"],
      captchaToken: null
    };
  },
  computed: {
    otherMagasins() {
      if (!this.magasinId) {
        return this.magasins;
      }
      return this.magasins.filter((magasin) => {
        return parseInt(magasin.id) !== this.magasinId;
      });
    },
    currentMagasin() {
      return this.magasins.find((magasin) => {
        return parseInt(magasin.id) === this.magasinId;
      });
    },
    cp() {
      return this.model.cp;
    }
  },
  watch: {
    currentMagasin: {
      handler(value) {
        if (value) {
          this.model.id_magasin = this.currentMagasin.id;
          this.v$.model.id_magasin.$touch();
        }
      },
      immediate: true
    },
    cp: {
      handler() {
        if (this.getState(this.v$, "cp", "model") === this.states.SUCCESS) {
          this.$emit("cp", this.model.cp);
        }
      },
      immediate: true
    }
  },
  methods: {
    verifyCaptcha(token) {
      if (token) {
        this.captchaToken = token;
      }
      const model = {
        ...this.model,
        nombre_enfants:
          this.model.nombre_enfants === "" ? 0 : this.model.nombre_enfants,
        "g-captcha-response": this.captchaToken
      };
      this.$emit("save", model);
    },
    save() {
      if (this.checkValidation(this.v$)) {
        this.$refs.captcha.execute();
      }
    }
  },
  validations() {
    return {
      model: {
        possede_carte_cora: {
          required
        },
        deja_possede_carte_malin: {
          required
        },
        id_magasin: {
          required
        },
        nom: {
          required
        },
        prenom: {
          required
        },
        civilite: {
          required
        },
        email: {
          required,
          email
        },
        email_confirmation: {
          required,
          sameAsEmail: sameAs(this.model.email)
        },
        tel: {
          required,
          tel
        },
        adresse: {
          required
        },
        cp: {
          required,
          zipCode
        },
        ville: {
          required
        },
        date_naissance: {
          birthdate
        },
        nombre_enfants: {
          numeric,
          maxValue: maxValue(10),
          minValue: minValue(0)
        },
        date_naissance_1er_enfant: {
          birthdate
        },
        date_naissance_2eme_enfant: {
          birthdate
        },
        date_naissance_3eme_enfant: {
          birthdate
        },
        date_naissance_4eme_enfant: {
          birthdate
        },
        optin_email_suivi_informations_promotionnelles_commerciales: {
          required
        },
        accepter_conditions_compte_client: {
          checked
        },
        accepter_conditions_fidelite: {
          checked
        }
      }
    };
  }
};
</script>
<template>
  <div class="u-mb-xlg u-pl-md u-pr-md u-bgcolor-white u-pb-md">
    <br>
    <h2 class="c-connexion-form__title">
      Chère cliente, cher client,
    </h2>
    <p class="c-inscription-form__explanation">
      Afin de mieux vous connaître et de vous faire profiter de nombreuses économies, nous vous remercions de bien vouloir remplir ce questionnaire.
      Votre demande sera transmise au magasin de votre choix et vous n'aurez plus qu'à vous présenter à l'espace cartes muni d'une pièce d'identité pour obtenir votre carte malin.
      Si vous avez déjà la carte cora, vous n'avez pas besoin de la carte malin, vous bénéficiez automatiquement de tous ces avantages.
    </p>
    <p class="c-inscription-form__explanation">
      Pour vous remettre la carte malin
    </p>
    <br>

    <p class="c-inscription-form__explanation">
      (*Les champs marqués d'un astérisque sont obligatoires.)
    </p>
    <br>
    <SuccessPopin
      v-if="state===states.SUCCESS"
      @hidden="redirectToPreviousPage()"
    />
    <FormError
      v-if="error"
      :error="error"
    />
    <Form
      class="u-mx-xxlg u-mx-lg@md"
      @submit.prevent="save()"
    >
      <FormFieldset
        center
        size="full"
      >
        <FormGroup
          :state="getState(v$,'id_magasin', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <label
            for="magasinCarte"
            class="u-block u-font-13 u-mb-xs u-color-text-light"
          > Votre magasin de référence*
          </label>
          <FormSelect
            id="magasinCarte"
            v-model="model.id_magasin"
            :state="getState(v$,'id_magasin', 'model')"
            :default-value="'Sélectionnez un magasin'"
            size="full"
          >
            <FormSelectOptGroup
              v-if="currentMagasin"
              label="Vous êtes rattaché(e) au magasin"
            >
              <FormSelectOption :value="currentMagasin.id">
                {{ currentMagasin.attributes.magasin }}
              </FormSelectOption>
            </FormSelectOptGroup>
            <FormSelectOptGroup label="Autres magasins Cora">
              <FormSelectOption
                v-for="magasin in otherMagasins"
                :key="magasin.id"
                :value="magasin.id"
              >
                {{ magasin.attributes.magasin }}
              </FormSelectOption>
            </FormSelectOptGroup>
          </FormSelect>
          <template #message>
            <p>
              <template v-if="model.id_magasin.required.$invalid">
                Merci de choisir un magasin
              </template>
              <template v-else>
                {{ getErrorMessage("id_magasin") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <label class="u-block u-font-13 u-mb-xs u-color-text-light">Possédez-vous déjà une Carte Cora ? *</label>
        <FormGroup
          :state="getState(v$,'possede_carte_cora', 'model')"
          inline
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormRadio
            v-model="model.possede_carte_cora"
            name="possede_carte_cora"
            :value="true"
            class="u-mr-lg"
          >
            Oui
          </FormRadio>
          <FormRadio
            v-model="model.possede_carte_cora"
            name="possede_carte_cora"
            :value="false"
          >
            Non
          </FormRadio>
          <template #message>
            <p>
              <template v-if="v$.model.possede_carte_cora.required.$invalid">
                Merci de renseigner ce champ
              </template>
              <template v-else>
                {{ getErrorMessage("possede_carte_cora") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <label class="u-block u-font-13 u-mb-xs u-color-text-light">Avez vous déja possédé une carte malin ? *</label>
        <FormGroup
          :state="getState(v$,'deja_possede_carte_malin', 'model')"
          inline
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormRadio
            v-model="model.deja_possede_carte_malin"
            name="deja_possede_carte_malin"
            :value="true"
            class="u-mr-lg u-flex-self-start"
          >
            Oui, mais je l’ai perdue
          </FormRadio>
          <FormRadio
            v-model="model.deja_possede_carte_malin"
            name="deja_possede_carte_malin"
            class="u-flex-self-start"
            :value="false"
          >
            Non
          </FormRadio>
          <template #message>
            <p>
              <template v-if="v$.model.deja_possede_carte_malin.required.$invalid">
                Merci de renseigner ce champ
              </template>
              <template v-else>
                {{ getErrorMessage("deja_possede_carte_malin") }}
              </template>
            </p>
          </template>
        </FormGroup>
        <label class="u-block u-font-13 u-mb-xs u-color-text-light">Civilité *</label>
        <FormGroup
          :state="getState(v$,'civilite', 'model')"
          inline
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormRadio
            v-model="model.civilite"
            name="civilite"
            value="Mme"
            class="u-mr-lg"
          >
            Madame
          </FormRadio>
          <FormRadio
            v-model="model.civilite"
            name="civilite"
            value="M."
          >
            Monsieur
          </FormRadio>
          <template #message>
            <p>
              <template v-if="v$.model.civilite.required.$invalid">
                Merci de renseigner votre civilité
              </template>
              <template v-else>
                {{ getErrorMessage("civilite") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'prenom', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="prenom"
            v-model.trim="model.prenom"
            :state="getState(v$,'prenom', 'model')"
            maxlength="40"
            size="full"
          >
            Prénom *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.model.prenom.required.$invalid">
                Merci de renseigner votre prénom
              </template>
              <template v-else>
                {{ getErrorMessage("prenom") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'nom', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="nom"
            v-model.trim="model.nom"
            :state="getState(v$,'nom', 'model')"
            size="full"
            @blur="v$.model.nom.$touch()"
            @input="v$.model.nom.$reset()"
          >
            Nom *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.model.nom.required.$invalid">
                Merci de renseigner votre nom
              </template>
              <template v-else>
                {{ getErrorMessage("nom") }}
              </template>
            </p>
          </template>
        </FormGroup>
        <FormGroup
          :state="getState(v$,'email','model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="email"
            v-model.trim="model.email"
            :state="getState(v$,'email','model')"
            size="full"
            @blur="v$.model.email.$touch()"
            @input="v$.model.email.$reset()"
          >
            Email *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.model.email.required.$invalid">
                Merci de confirmer votre adresse e-mail
              </template>
              <template v-else-if="v$.model.email.email.$invalid">
                Le format de cette adresse e-mail est invalide
              </template>
              <template v-else>
                {{ getErrorMessage("email") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'email_confirmation', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="confirmEmail"
            v-model.trim="model.email_confirmation"
            :state="getState(v$,'email_confirmation', 'model')"
            size="full"
            @blur="v$.model.email_confirmation.$touch()"
            @input="v$.model.email_confirmation.$reset()"
          >
            Confirmation adresse email *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.model.email_confirmation.required.$invalid">
                Merci de confirmer votre adresse e-mail
              </template>
              <template v-else-if="v$.model.email_confirmation.email.$invalid">
                Le format de cette adresse e-mail est invalide
              </template>
              <template v-else-if="v$.model.email_confirmation.sameAs.$invalid">
                Les adresses mails que vous avez saisi ne sont pas identiques
              </template>
              <template v-else>
                Le format de cette adresse e-mail est invalide
              </template>
            </p>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'adresse', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="adresse"
            v-model.trim="model.adresse"
            :state="getState(v$,'adresse', 'model')"
            maxlength="60"
            size="full"
            @blur="v$.model.adresse.$touch()"
            @input="v$.model.adresse.$reset()"
          >
            Adresse *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.model.adresse.required.$invalid">
                Merci de renseigner votre adresse
              </template>
              <template v-else>
                {{ getErrorMessage("adresse") }}
              </template>
            </p>
          </template>
        </FormGroup>
        <FormGroup
          :state="getState(v$,'complement_adresse', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="complement"
            v-model.trim="model.complement_adresse"
            :state="getState(v$,'complement_adresse', 'model')"
            size="full"
            @blur="v$.model.adresse.$touch()"
            @input="v$.model.adresse.$reset()"
          >
            Complément
          </FormInput>
          <template #message>
            <p>
              {{ getErrorMessage("complement_adresse") }}
            </p>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'cp', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="cp"
            v-model="model.cp"
            name="postal-code"
            autocomplete="postal-code"
            pattern="[0-9]*"
            :state="getState(v$,'cp', 'model')"
            type="tel"
            size="full"
            @blur="v$.model.cp.$touch()"
            @input="v$.model.cp.$reset()"
          >
            Code Postal *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.model.cp.required.$invalid">
                Merci de renseigner votre code postal
              </template>
              <template v-else>
                {{ getErrorMessage("cp","Veuillez entrer un code postal valide : il doit comporter entre 3 et 10 caractères") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'ville', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="ville"
            v-model.trim="model.ville"
            :state="getState(v$,'ville', 'model')"
            size="full"
            @blur="v$.model.ville.$touch()"
            @input="v$.model.ville.$reset()"
          >
            Ville *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.model.ville.required.$invalid">
                Merci de renseigner votre Ville
              </template>
              <template v-else>
                {{ getErrorMessage("ville") }}
              </template>
            </p>
          </template>
        </Formgroup>
        <label
          for="paysCarte"
          class="u-block u-font-13 u-mb-xs u-color-text-light"
        >Pays</label>
        <FormGroup
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormSelect
            id="paysCarte"
            v-model="model.pays"
            size="full"
          >
            <FormSelectOption
              v-for="country in countries"
              :key="country"
              :value="country"
            >
              {{ country }}
            </FormSelectOption>
          </FormSelect>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'tel', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="tel"
            v-model.trim="model.tel"
            :state="getState(v$,'tel', 'model')"
            size="full"
            @blur="v$.model.tel.$touch()"
            @input="v$.model.tel.$reset()"
          >
            Téléphone *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.model.tel.required.$invalid">
                Merci de renseigner votre numéro de télephone
              </template>
              <template v-else-if="v$.model.tel.tel.$invalid">
                Ce numéro de téléphone est invalide
              </template>
              <template v-else>
                {{ getErrorMessage("tel") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <br>
        <p class="c-inscription-form__explanation">
          Pour mieux vous connaitre :
        </p>
        <br>
        <label class="u-block u-font-13 u-mb-xs u-color-text-light">
          Date de naissance
        </label>
        <FormGroup
          :state="getState(v$,'date_naissance','model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="date"
            v-model.trim="model.date_naissance"
            type="date"
            autocomplete
            :state="getState(v$,'date_naissance','model')"
            size="full"
            @blur="v$.model.date_naissance.$touch()"
            @input="v$.model.date_naissance.$reset()"
          />
          <template #message>
            <p>
              <template v-if="v$.model.date_naissance.birthdate.$invalid">
                Merci de saisir une date valide
              </template>
              <template v-else>
                {{ getErrorMessage("date_naissance") }}
              </template>
            </p>
          </template>
        </FormGroup>
        <FormGroup
          :state="getState(v$,'nombre_enfants', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="nbEnfants"
            v-model.trim="model.nombre_enfants"
            :state="getState(v$,'nombre_enfants', 'model')"
            type="number"
            :min="0"
            size="full"
          >
            Nombre d’enfants
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.model.nombre_enfants.lessThanTen.$invalid">
                la valeur maximale possible est 10
              </template>
              <template v-else>
                {{ getErrorMessage("nombre_enfants") }}
              </template>
            </p>
          </template>
        </FormGroup>
        <br>
        <label class="u-block u-font-13 u-mb-xs u-color-text-light">
          Date de naissance du 1er enfant
        </label>
        <FormGroup
          :state="getState(v$,'date_naissance_1er_enfant', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="date1"
            v-model.trim="model.date_naissance_1er_enfant"
            type="date"
            :state="getState(v$,'date_naissance_1er_enfant', 'model')"
            size="full"
            @blur="v$.model.date_naissance_1er_enfant.$touch()"
            @input="v$.model.date_naissance_1er_enfant.$reset()"
          />
          <template #message>
            <p>
              <template v-if="v$.model.date_naissance_1er_enfant.birthdate.$invalid">
                Merci de saisir une date valide
              </template>
              <template v-else>
                {{ getErrorMessage("date_naissance_1er_enfant") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <label class="u-block u-font-13 u-mb-xs u-color-text-light">
          Date de naissance du 2eme enfant</label>
        <FormGroup
          :state="getState(v$,'date_naissance_2eme_enfant', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="date2"
            v-model.trim="model.date_naissance_2eme_enfant"
            type="date"
            :state="getState(v$,'date_naissance_2eme_enfant', 'model')"
            size="full"
            @blur="v$.model.date_naissance_2eme_enfant.$touch()"
            @input="v$.model.date_naissance_2eme_enfant.$reset()"
          />
          <template #message>
            <p>
              <template v-if="v$.model.date_naissance_2eme_enfant.birthdate.$invalid">
                Merci de saisir une date valide
              </template>
              <template v-else>
                {{ getErrorMessage("date_naissance_2eme_enfant") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <label class="u-block u-font-13 u-mb-xs u-color-text-light">Date de naissance du 3eme enfant</label>

        <FormGroup
          :state="getState(v$,'date_naissance_3eme_enfant', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="date3"
            v-model.trim="model.date_naissance_3eme_enfant"
            type="date"
            :state="getState(v$,'date_naissance_3eme_enfant', 'model')"
            size="full"
            @blur="v$.model.date_naissance_3eme_enfant.$touch()"
            @input="v$.model.date_naissance_3eme_enfant.$reset()"
          />
          <template #message>
            <p>
              <template v-if="v$.model.date_naissance_3eme_enfant.birthdate.$invalid">
                Merci de saisir une date valide
              </template>
              <template v-else>
                {{ getErrorMessage("date_naissance_3eme_enfant") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <label class="u-block u-font-13 u-mb-xs u-color-text-light">
          Date de naissance du 4eme enfant</label>

        <FormGroup
          :state="getState(v$,'date_naissance_4eme_enfant', 'model')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="date4"
            v-model.trim="model.date_naissance_4eme_enfant"
            type="date"
            :state="getState(v$,'date_naissance_4eme_enfant', 'model')"
            size="full"
            @blur="v$.model.date_naissance_4eme_enfant.$touch()"
            @input="v$.model.date_naissance_4eme_enfant.$reset()"
          />
          <template #message>
            <p>
              <template v-if="v$.model.date_naissance_4eme_enfant.birthdate.$invalid">
                Merci de saisir une date valide
              </template>
              <template v-else>
                {{ getErrorMessage("date_naissance_4eme_enfant") }}
              </template>
            </p>
          </template>
        </FormGroup>
      </FormFieldset>

      <FormGroup
        size="full"
        class="c-inscription-form__form-group"
      >
        <FormCheckbox v-model="model.optin_email_suivi_informations_promotionnelles_commerciales">
          J’accepte de recevoir par e-mail des informations promotionnelles et offres commerciales transmises par Cora.
        </FormCheckbox>
      </FormGroup>

      <FormGroup
        size="full"
        class="c-inscription-form__form-group"
        :state="getState(v$,'accepter_conditions_fidelite', 'model')"
      >
        <FormCheckbox
          v-model.trim="model.accepter_conditions_fidelite"
          :state="getState(v$,'accepter_conditions_fidelite', 'model')"
          @blur="v$.model.accepter_conditions_fidelite.$touch()"
          @input="v$.model.accepter_conditions_fidelite.$reset()"
        >
          <span>
            En cochant cette case, vous acceptez <LinkTo
              target="_blank"
              to="/legal/informations-juridiques/conditions-generales-fidelite"
            >
              les Conditions générales
              du programme de fidélité de Cora</LinkTo> *
          </span>
        </FormCheckbox>
        <template #message>
          <p>
            <template v-if="v$.model.accepter_conditions_fidelite.checked.$invalid">
              Vous devez accepter les les Conditions générales du programme de fidélité de Cora pour pouvoir soumettre votre demande
            </template>
          </p>
        </template>
      </FormGroup>
      <FormGroup
        size="full"
        class="c-inscription-form__form-group"
        :state="getState(v$,'accepter_conditions_compte_client', 'model')"
      >
        <FormCheckbox
          v-model.trim="model.accepter_conditions_compte_client"
          :state="getState(v$,'accepter_conditions_compte_client', 'model')"
          @blur="v$.model.accepter_conditions_compte_client.$touch()"
          @input="v$.model.accepter_conditions_compte_client.$reset()"
        >
          <span>
            En cochant cette case, vous acceptez <LinkTo
              target="_blank"
              to="/legal/informations-juridiques/cgu-compte-client-internet"
            >
              les Conditions générales d’utilisation du Compte Client Internet
            </LinkTo> *
          </span>
        </FormCheckbox>
        <template #message>
          <p>
            <template v-if="v$.model.accepter_conditions_compte_client.checked.$invalid">
              Vous devez accepter les les Conditions générales d’utilisation du Compte Client Internet pour pouvoir soumettre votre demande
            </template>
          </p>
        </template>
      </FormGroup>
      <Captcha
        ref="captcha"
        @verify="verifyCaptcha"
      />
      <ButtonGroup center>
        <Button
          variant="secondary"
          radius="full"
          type="submit"
          value="Je valide ma demande"
          size="md"
          class="u-mt-sm u-ml-lg u-flex-justify-center@sm "
          :disabled="state === states.LOADING"
        />
      </ButtonGroup>
    </Form>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/components/inscription-form.scss";
</style>
